import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@center-pwa/store/store';
import { selectSphereId } from '@center-pwa/selectors/selectors';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { MetaspaceDefinition } from '../../organisms/MetaspaceDefinitions';
import AtlasComponent from '../../organisms/AtlasComponent/AtlasComponent';
import MetaspaceToolbar, { type ToolbarItem } from '../../organisms/metaspace-toolbar/metaspacetoolbar';
import UserListOrganism from '../../organisms/UserList/UserListOrganism';
import PreferencesOrganism from '../../organisms/PreferencesOrganism/PreferencesOrganism';
import ProfileOrganism from '../../organisms/ProfileOrganism/ProfileOrganism';
import SecurityOrganism from '../../organisms/SecurityOrganism/SecurityOrganism';
import TreeMapOrganism from '../../organisms/TreeMapOrganism/TreeMapOrganism';
import ArmillaryViewOrganism from '../../organisms/ArmillaryView/ArmillaryViewOrganism';
import ArmillaryActionsMolecule from '../ArmillaryActions/ArmillaryActionsMolecule';
import TaskListOrganism from '../../organisms/TaskList/TaskListOrganism';
import ScorecardOrganism from '../../organisms/ScorecardOrganism/ScorecardOrganism';
import { Task } from '../../molecules/TaskList/TaskListMolecule.types';
import EditSphereLauncherMolecule from '../EditSphereLauncher/EditSphereLauncherMolecule';
import { useEditSphereLauncher, type FetchPermissionList } from '../EditSphereLauncher/useEditSphereLauncher';
import type { SphereChange } from '../../../hooks/useSphereChangeEffect';
import { ToolbarStateProvider } from '../../../context/ToolbarStateContext';
import { ArmillaryProvider } from '../../../context/ArmillaryContext';
import { ToolbarProvider } from '../../../context/ToolbarContext';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { FALLBACK_METASPACE_SYSTEM_NAME } from '../../../constants/metaspaces';
import { getSphereById } from '@center-platform/spheres/sphere';
import type { Sphere as GlobalSphere } from 'types/global';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useTranslation } from 'localization';
import './MetaspaceGrid.scss';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface MetaspaceGridProps {
  definition: MetaspaceDefinition;
  toolbarOpen?: boolean;
  onToolbarOpenChange?: (isOpen: boolean) => void;
  toolbarActiveItem?: ToolbarItem;
  onToolbarActiveItemChange?: (item: ToolbarItem) => void;
}

const MetaspaceGrid: React.FC<MetaspaceGridProps> = ({
  definition,
  toolbarOpen = false,
  onToolbarOpenChange,
  toolbarActiveItem = 'chatbox',
  onToolbarActiveItemChange,
}) => {
  const { layout, settings } = definition;
  const sphereId = useSelector(selectSphereId);
  const [rowHeight, setRowHeight] = useState(30);

  // Mobile detection
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Sphere ID for chat context
  const [selectedSphereId, setSelectedSphereId] = useState<string>();
  // Sphere editing (dialog state + fetch/permission handlers) shared with SpaceHost
  const {
    onEditSphere,
    fetchPermissionList,
    sphereToEdit,
    visible: editSphereDialogVisible,
    onSave,
    onSphereRefresh,
    onClose,
    sphereChange,
  } = useEditSphereLauncher();

  // Check if metaspace has an Atlas or ArmillaryView organism
  const hasAtlasOrganism = layout.some(organism => organism.type === 'Atlas');
  const hasArmillaryOrganism = layout.some(organism => organism.type === 'ArmillaryView');

  // Check if toolbar should be shown (metaspace-level concern)
  const showToolbar = (hasAtlasOrganism || hasArmillaryOrganism) && settings?.showMetasphereToolbar !== false;

  // ArmillaryProvider is only needed when we have ArmillaryView (orbital display).
  // Toolbar data (notifications/tasks/timeline) is now owned by ToolbarProvider.
  const needsArmillaryContext = hasArmillaryOrganism;

  // Toolbar configuration from metaspace template settings
  const toolbarDefaultOpen = settings?.toolbarDefaultOpen ?? false;
  const toolbarDefaultActiveItem = settings?.toolbarDefaultActiveItem as ToolbarItem | undefined;
  const enabledToolbarItems = settings?.enabledToolbarItems;
  const disabledToolbarItems = settings?.disabledToolbarItems;
  const chatboxContextMode = settings?.toolbarItemSettings?.chatbox?.contextMode;

  useEffect(() => {
    const updateRowHeight = () => {
      // Get the actual layout container element where grid items are placed
      const layoutContainer = document.querySelector('.layout');
      let availableHeight = 600; // Fallback minimum height

      if (layoutContainer) {
        // Use the layout container's actual height
        availableHeight = layoutContainer.clientHeight;
      }

      // Calculate row height based on grid height
      const gridHeight = layout.reduce((max, item) => Math.max(max, item.gridPosition.y + item.gridPosition.height), 0);

      // Ensure we have a minimum grid height to avoid division by zero or very large values
      const safeGridHeight = Math.max(gridHeight, 1);

      // React-grid-layout: containerPadding [10, 10] and margin [10, 10] (consistent gutters)
      const containerPaddingY = 10; // top padding
      const containerPaddingBottom = 10; // bottom padding
      const marginY = 10; // vertical margin between rows

      // Account for container padding and margins between rows
      // Formula: rowHeight * rows + (rows - 1) * marginY + containerPaddingY + containerPaddingBottom = availableHeight
      // Solve for rowHeight: rowHeight = (availableHeight - containerPaddingY - containerPaddingBottom - (rows - 1) * marginY) / rows
      const heightForRows = availableHeight - containerPaddingY - containerPaddingBottom - (safeGridHeight - 1) * marginY;
      const calculatedRowHeight = Math.max(30, Math.floor(heightForRows / safeGridHeight));

      setRowHeight(calculatedRowHeight);
    };

    // Delay the initial calculation to allow flex containers to settle
    const initialTimer = setTimeout(updateRowHeight, 100);
    window.addEventListener('resize', updateRowHeight);

    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener('resize', updateRowHeight);
    };
  }, [layout]);

  // Map the organism definitions to the layout format required by react-grid-layout.
  const gridLayout = layout.map((organism, index) => {
    const itemDraggable = !isMobile && (organism.settings?.draggable ?? true);
    const itemResizable = !isMobile && (organism.settings?.collapsible ?? true);

    return {
      i: index.toString(),
      x: organism.gridPosition.x,
      y: organism.gridPosition.y,
      w: organism.gridPosition.width,
      h: organism.gridPosition.height,
      isDraggable: itemDraggable,
      isResizable: itemResizable,
    };
  });

  // Define breakpoints and corresponding column counts for responsive behavior.
  const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
  const cols = { lg: 12, md: 10, sm: 8, xs: 4, xxs: 2 };

  const handleChatBubbleClick = (sphereId: string) => {
    setSelectedSphereId(sphereId);
    onToolbarActiveItemChange?.('chatbox');
    onToolbarOpenChange?.(true);
  };

  const computedIsDraggable = !isMobile && (settings?.editable ?? true);
  const computedIsResizable = !isMobile && (settings?.editable ?? true);

  const gridContent = (
    <ToolbarStateProvider initialExpanded={toolbarOpen}>
      <div className="metaspace-grid-container" style={{ display: 'flex', flexDirection: 'row', height: '100%', width: '100%' }}>
        {showToolbar && !isMobile && (
          <MetaspaceToolbar
            onOpenChange={onToolbarOpenChange}
            chatSphereId={selectedSphereId}
            items={enabledToolbarItems as ToolbarItem[] | undefined}
            disabledItems={disabledToolbarItems}
            defaultOpen={toolbarDefaultOpen}
            defaultActiveItem={toolbarDefaultActiveItem}
            activeSphereId={sphereId || undefined}
            chatboxContextMode={chatboxContextMode}
            onEditSphere={onEditSphere}
          />
        )}
        <div
          style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingBottom: '20px' }}
          role="region"
          aria-label="Metaspace grid layout"
        >
          <ResponsiveGridLayout
            className="layout"
            layouts={{ lg: gridLayout }}
            breakpoints={breakpoints}
            cols={cols}
            isDraggable={computedIsDraggable}
            isResizable={computedIsResizable}
            resizeHandles={['se']}
            draggableCancel=".no-drag"
            rowHeight={rowHeight}
            margin={[10, 10]}
          >
            {layout.map((organism, index) => {
              // Hide ArmillaryActions grid item when toolbar is open
              const shouldHideGridItem = organism.type === 'ArmillaryActions' && toolbarOpen;

              return (
                <div
                  key={index.toString()}
                  className={`grid-item ${shouldHideGridItem ? 'grid-item--hidden' : ''}`}
                  style={shouldHideGridItem ? { display: 'none' } : undefined}
                >
                  <OrganismRenderer
                    organism={organism}
                    definition={definition}
                    sphereId={sphereId || ''}
                    onChatBubbleClick={handleChatBubbleClick}
                    showToolbar={showToolbar}
                    toolbarOpen={toolbarOpen}
                    onEditSphere={onEditSphere}
                    sphereChange={sphereChange}
                    fetchPermissionList={fetchPermissionList}
                  />
                </div>
              );
            })}
          </ResponsiveGridLayout>
        </div>
      </div>

      <EditSphereLauncherMolecule
        sphereToEdit={sphereToEdit}
        visible={editSphereDialogVisible}
        onSave={onSave}
        onSphereRefresh={onSphereRefresh}
        onClose={onClose}
        fetchPermissionList={fetchPermissionList}
      />
    </ToolbarStateProvider>
  );

  // Wrap with ToolbarProvider when toolbar is shown (owns notification/task/timeline fetching)
  const withToolbar = showToolbar
    ? <ToolbarProvider initialSphereId={sphereId || undefined} sphereChange={sphereChange}>{gridContent}</ToolbarProvider>
    : gridContent;

  if (needsArmillaryContext) {
    return <ArmillaryProvider initialRootSphereId={sphereId || undefined}>{withToolbar}</ArmillaryProvider>;
  }

  return withToolbar;
};

// React component to render organisms
interface OrganismRendererProps {
  organism: any;
  definition: MetaspaceDefinition;
  sphereId: string;
  onChatBubbleClick?: (sphereId: string) => void;
  showToolbar: boolean; // Whether to show toolbar/chat based on showMetasphereToolbar setting
  toolbarOpen?: boolean; // Whether the left toolbar is currently open
  onEditSphere?: (taskId: string, task: Task) => void;
  /** The last sphere the grid's editor changed; the task list re-reads it. */
  sphereChange?: SphereChange | null;
  fetchPermissionList?: FetchPermissionList;
}

const OrganismRenderer: React.FC<OrganismRendererProps> = ({ organism, definition, sphereId, onChatBubbleClick, showToolbar, toolbarOpen, onEditSphere, sphereChange, fetchPermissionList: fetchPermissionListProp }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiClients = useApiClientsOrNull();
  const organizationId = useSelector((state: RootState) => state.userProfile?.userSphere?.properties?.sphereId) ?? '';
  const identityId = useSelector((state: RootState) => state.userProfile.identity?.identity?.id) ?? '';
  const userId = useSelector((state: RootState) => state.userProfile.userId) ?? '';

  // State for TreeMap template override (when user selects a different template)
  const [treeMapTemplateOverride] = useState<string | null>(null);

  // Resolve which sphere a Scorecard organism roots its context at, using the
  // same sphereIdSource convention as the case below — for Monthly Ops Review
  // this is the orbit sphere. Computed unconditionally (hooks can't be
  // conditional) so it's a no-op for every other organism type.
  const scorecardSettings = organism.type === 'Scorecard' ? organism.scorecardSettings || {} : {};
  const scorecardOrbitSphereId: string | undefined = useMemo(() => {
    if (organism.type !== 'Scorecard') return undefined;
    if (organism.sphereIdSource === 'identity') return identityId;
    if (organism.sphereIdSource === 'organization') return organizationId;
    if (organism.sphereIdSource === 'current' || organism.sphereIdSource === 'sphere') return sphereId;
    if (scorecardSettings.sphereId || organism.sphereId) return scorecardSettings.sphereId || organism.sphereId;
    return sphereId;
  }, [organism, identityId, organizationId, sphereId, scorecardSettings.sphereId]);

  // Fetch the orbit sphere (with its resolved center_center assignment) so
  // ScorecardOrganism can offer the Meeting Decisions affordance. Read-only
  // scorecards (no matching orbit) simply never populate this and the
  // affordance stays hidden — ScorecardOrganism treats orbitSphere as optional.
  //
  // Deliberate exception to /standards/frontend/api-clients.md Rule 6
  // (prefer globalSphereCache.getSphereData): the affordance needs the orbit's
  // getAssignedPerspectives=true read to resolve center_center, and the cache
  // only surfaces basic sphere info (id/label/name/glyph/avatar), never
  // assigned perspectives — so the batched cache path cannot serve this read.
  const [scorecardOrbitSphere, setScorecardOrbitSphere] = useState<GlobalSphere | null>(null);

  useEffect(() => {
    if (!scorecardOrbitSphereId || !apiClients?.graphClient) {
      setScorecardOrbitSphere(null);
      return;
    }
    let cancelled = false;
    getSphereById(apiClients.graphClient, scorecardOrbitSphereId, true, false)
      .then((response) => {
        if (!cancelled) setScorecardOrbitSphere((response.data?.sphere as GlobalSphere) ?? null);
      })
      .catch((err) => {
        console.error('[MetaspaceGrid] Error fetching orbit sphere for scorecard:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [scorecardOrbitSphereId, apiClients]);

  switch (organism.type) {
    case 'Atlas': {
      // Determine sphereId based on sphereIdSource
      let atlasSphereId: string | undefined;
      if (organism.sphereIdSource === 'identity') {
        atlasSphereId = identityId;
      } else if (organism.sphereIdSource === 'organization') {
        atlasSphereId = organizationId;
      } else if (organism.sphereId) {
        atlasSphereId = organism.sphereId;
      }

      return (
        <AtlasComponent
          contextSystemName={organism.contextSystemName ?? ''}
          viewSystemName={organism.viewSystemName}
          sphereId={atlasSphereId}
          organizationId={organizationId}
          onChatBubbleClick={onChatBubbleClick}
          showToolbar={showToolbar}
          onNewContext={() => navigate('/studio/context/new')}
          onManageContexts={() => navigate('/studio/context')}
          onEditContext={(systemName) => navigate(`/studio/context/${systemName}`)}
        />
      );
    }
    case 'Users':
      // Root the user list at the sphere being viewed (the org this metaspace is
      // deployed on), NOT the caller's home org (userSphere.sphereId). The latter
      // showed the wrong org's users (and, under impersonation, an org outside the
      // token's tenant → empty) whenever you viewed another org's metaspace.
      return sphereId ? <UserListOrganism organizationId={sphereId} /> : <div>{t('common.loadingOrganization')}</div>;
    case 'Profile':
      return identityId ? <ProfileOrganism identityId={identityId} organizationId={organizationId} /> : <div>{t('common.loadingIdentity')}</div>;
    case 'Preferences':
      return identityId ? <PreferencesOrganism identityId={identityId} /> : <div>{t('common.loadingIdentity')}</div>;
    case 'Security':
      return identityId ? <SecurityOrganism identityId={identityId} /> : <div>{t('common.loadingIdentity')}</div>;
    case 'TreeMap': {
      // TreeMap organism for hierarchical visualization
      const config = organism.config || {};

      // If no rootSphereId is specified in config, use the current sphere from Redux
      const rootSphereId = config.rootSphereId || sphereId;

      // Use override if user selected one, otherwise use config
      const contextSystemName = treeMapTemplateOverride || config.contextSystemName;

      return (
        <TreeMapOrganism
          contextSystemName={contextSystemName}
          organizationId={organizationId}
          rootSphereId={rootSphereId}
          viewSystemName={config.viewSystemName}
          editable={config.editable ?? true}
          initialCardView={config.initialCardView || 'standard'}
          showBreadcrumbs={config.showBreadcrumbs ?? true}
          showFilters={config.showFilters ?? true}
          showAI={config.showAI ?? false}
          defaultPageSize={config.defaultPageSize || 10}
          useGridLayout={config.useGridLayout ?? true}
          onNewContext={() => navigate('/studio/context/new')}
          onManageContexts={() => navigate('/studio/context')}
        />
      );
    }
    case 'ArmillaryView':
      return (
        <ArmillaryViewOrganism
          sphereId={sphereId}
          centerImagePath={organism.centerImagePath}
          onNodeSelect={(sphere) => {
            if (sphere?.id) {
              navigate(`/sphere/${sphere.id}/metaspace/${FALLBACK_METASPACE_SYSTEM_NAME}`);
            }
          }}
          onConversationClick={() => {
            onChatBubbleClick?.(sphereId);
          }}
          onEditContext={(systemName) => navigate(`/studio/context/${systemName}`)}
        />
      );
    case 'ArmillaryActions':
      return (
        <ArmillaryActionsMolecule
          rootSphereId={sphereId || ''}
          activeSphereId={sphereId || ''}
          isHidden={toolbarOpen}
        />
      );
    case 'task-list': {
      // Task List organism for displaying unified tasks
      const taskListSettings = organism.taskListSettings || {};

      // Determine the sphereId to use for fetching tasks
      // Use sphereIdSource pattern like Atlas for flexibility
      let taskListSphereId: string | undefined;
      if (organism.sphereIdSource === 'identity') {
        taskListSphereId = identityId;
      } else if (organism.sphereIdSource === 'user') {
        taskListSphereId = userId;
      } else if (organism.sphereIdSource === 'organization') {
        taskListSphereId = organizationId;
      } else if (organism.sphereIdSource === 'current' || organism.sphereIdSource === 'sphere') {
        taskListSphereId = sphereId;
      } else if (taskListSettings.sphereId) {
        taskListSphereId = taskListSettings.sphereId;
      } else {
        // Default: use userId (user sphere) for "my tasks" view
        // This is where ASSIGNED_TO edges with center_responsible are attached
        taskListSphereId = userId;
      }

      if (!taskListSphereId) {
        return <div>{t('common.loadingTaskList')}</div>;
      }

      return (
        <TaskListOrganism
          sphereId={taskListSphereId}
          title={organism.title || t('myTasks.title')}
          collapsible={organism.settings?.collapsible ?? true}
          maxRows={taskListSettings.maxRows}
          onEditSphere={onEditSphere}
          sphereChange={sphereChange}
          fetchPermissionList={fetchPermissionListProp}
          settings={{
            showPriority: taskListSettings.showPriority,
            showAssignee: taskListSettings.showAssignee,
            showDueDate: taskListSettings.showDueDate,
            enableInlineActions: taskListSettings.enableInlineActions ?? true,
            refreshInterval: taskListSettings.refreshInterval,
            filterByStatus: taskListSettings.filterByStatus,
            filterByTaskType: taskListSettings.filterByTaskType,
            filterByCheckInStatus: taskListSettings.filterByCheckInStatus,
            includeVirtual: taskListSettings.includeVirtual,
            lookaheadDays: taskListSettings.lookaheadDays,
            sortBy: taskListSettings.sortBy,
            sortOrder: taskListSettings.sortOrder,
          }}
        />
      );
    }
    case 'Scorecard': {
      // Scorecard organism for status/velocity/gravity rollups. Follows the same
      // sphereIdSource convention as Atlas/task-list to resolve which sphere/orbit
      // the scorecard's context should be rooted at (the v1 IA gap this closes).
      // scorecardOrbitSphereId/scorecardOrbitSphere are resolved above (outside the
      // switch) since they're backed by hooks.
      if (!scorecardOrbitSphereId || !scorecardSettings.viewSystemName) {
        return <div>{t('common.loadingOrganization')}</div>;
      }

      return (
        <ScorecardOrganism
          viewSystemName={scorecardSettings.viewSystemName}
          rootSphereId={scorecardOrbitSphereId}
          orbitSphere={scorecardOrbitSphere ?? undefined}
          organizationId={organizationId}
        />
      );
    }
    default:
      return <div>{t('common.error.unknownOrganismType', { type: organism.type })}</div>;
  }
};

export default MetaspaceGrid;
